import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { documentsAPI, filesPlatformAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import PageHeader from '../components/PageHeader';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import ModalShell from '../components/ModalShell';
import MmSelect from '../components/MmSelect';
import Pagination from '../components/Pagination';
import TableListCheckbox from '../components/TableListCheckbox';
import { useAuth } from '../contexts/AuthContext';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';
import { searchFieldsMatch } from '../utils/searchText';

const Documents = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { success, error } = useToast();
  const { confirm } = useConfirmDialog();
  const { hasPermission, role } = usePermissions();
  const { user } = useAuth();
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVisibility, setSelectedVisibility] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    file_url: '',
    is_public: false
  });
  const [docTab, setDocTab] = useState('entrea');
  const [platformFiles, setPlatformFiles] = useState([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformUploading, setPlatformUploading] = useState(false);
  const canBulkPlatformFiles = hasPermission('documents.delete');
  const [selectedPlatformFileIds, setSelectedPlatformFileIds] = useState([]);
  // En mode membre (incl. officers en mode membre), pas de création de document.
  const canCreateDocument = !isMembre && hasPermission('documents.create');

  useEffect(() => {
    loadDocuments();
  }, [documentsPage, itemsPerPage]);

  useEffect(() => {
    if (docTab !== 'platform') return;
    const loadPlatform = async () => {
      try {
        setPlatformLoading(true);
        const res = await filesPlatformAPI.list({ limit: 100, skip: 0 });
        setPlatformFiles(res.data?.files || []);
      } catch (err) {
        console.error('Fichiers plateforme:', err);
        error(t('documentsPage.loadPlatformError'));
        setPlatformFiles([]);
      } finally {
        setPlatformLoading(false);
      }
    };
    loadPlatform();
  }, [docTab, t, error]);

  useEffect(() => {
    setSelectedPlatformFileIds([]);
  }, [docTab]);

  useEffect(() => {
    // En mode membre (incl. officers en mode membre), on force la bibliothèque Entrea.
    if (isMembre && docTab !== 'entrea') {
      setDocTab('entrea');
    }
  }, [isMembre, docTab]);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchQuery, selectedCategory, selectedVisibility]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (documentsPage - 1) * itemsPerPage
      };
      const response = await documentsAPI.getAll(params);
      
      // Gérer les deux formats de réponse
      if (Array.isArray(response.data)) {
        setDocuments(response.data);
        setFilteredDocuments(response.data);
        setDocumentsTotal(response.data.length);
      } else {
        setDocuments(response.data.documents || []);
        setFilteredDocuments(response.data.documents || []);
        setDocumentsTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
      error(t('documentsPage.loadDocumentsError'));
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category?.toLowerCase() === selectedCategory.toLowerCase());
    }

    // Filtre par visibilité
    if (selectedVisibility === 'public') {
      filtered = filtered.filter(doc => doc.is_public === true);
    } else if (selectedVisibility === 'private') {
      filtered = filtered.filter(doc => doc.is_public === false);
    }

    // Recherche par titre ou description
    if (searchQuery.trim()) {
      filtered = filtered.filter((doc) =>
        searchFieldsMatch(searchQuery, [doc.title, doc.description])
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedDocument) {
        await documentsAPI.update(selectedDocument.id, formData);
        success(t('documentsPage.updateOk'));
      } else {
        await documentsAPI.create(formData);
        success(t('documentsPage.createOk'));
      }
      
      loadDocuments();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save document:', err);
      error(t('documentsPage.saveError'));
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: t('documentsPage.confirmDeleteDocTitle'),
      message: t('documentsPage.confirmDeleteDocMessage'),
      confirmText: t('documentsPage.confirmDelete'),
      cancelText: t('documentsPage.cancel'),
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await documentsAPI.delete(id);
      success(t('documentsPage.deleteOk'));
      loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      error(t('documentsPage.deleteError'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      file_url: '',
      is_public: false
    });
    setSelectedDocument(null);
  };

  const getCategoryIcon = (category) => {
    const safeCategory = String(category || '').toLowerCase();
    switch (safeCategory) {
      case 'rapport':
        return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
      case 'contrat':
        return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
      case 'financier':
        return 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      default:
        return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
    }
  };

  const handlePlatformUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPlatformUploading(true);
      await filesPlatformAPI.upload(file);
      success(t('documentsPage.platformFileUploaded'));
      const res = await filesPlatformAPI.list({ limit: 100, skip: 0 });
      setPlatformFiles(res.data?.files || []);
    } catch (err) {
      console.error(err);
      error(err.response?.data?.detail || t('documentsPage.platformUploadFail'));
    } finally {
      setPlatformUploading(false);
      e.target.value = '';
    }
  };

  const handlePlatformDelete = async (fileId) => {
    const confirmed = await confirm({
      title: t('documentsPage.confirmDeleteFileTitle'),
      message: t('documentsPage.confirmDeleteFileMessage'),
      confirmText: t('documentsPage.confirmDelete'),
      cancelText: t('documentsPage.cancel'),
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      await filesPlatformAPI.delete(fileId);
      success(t('documentsPage.platformFileDeleted'));
      setPlatformFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      error(err.response?.data?.detail || t('documentsPage.platformFileDeleteError'));
    }
  };

  const handleSelectAllPlatformFiles = (e) => {
    if (e.target.checked) {
      setSelectedPlatformFileIds(platformFiles.map((f) => f.id));
    } else {
      setSelectedPlatformFileIds([]);
    }
  };

  const togglePlatformFileSelection = (id) => {
    setSelectedPlatformFileIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkPlatformDelete = async () => {
    const ids = selectedPlatformFileIds.filter((id) => platformFiles.some((f) => f.id === id));
    if (ids.length === 0) return;
    const confirmed = await confirm({
      title: t('documentsPage.bulkDeleteTitle'),
      message: t('documentsPage.bulkDeleteMessage', { count: ids.length }),
      confirmText: t('documentsPage.confirmDelete'),
      cancelText: t('documentsPage.cancel'),
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      for (const id of ids) {
        await filesPlatformAPI.delete(id);
      }
      success(t('documentsPage.bulkDeleteOk', { count: ids.length }));
      setSelectedPlatformFileIds([]);
      setPlatformFiles((prev) => prev.filter((f) => !ids.includes(f.id)));
    } catch (err) {
      error(err.response?.data?.detail || t('documentsPage.bulkDeleteError'));
    }
  };

  const documentsTotalPages = Math.max(1, Math.ceil(documentsTotal / itemsPerPage));
  const renderDocumentsPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={documentsPage}
      totalPages={documentsTotalPages}
      totalItems={documentsTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setDocumentsPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setDocumentsPage(1);
      }}
    />
  );

  if (loading && docTab === 'entrea') {
    return (
      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        <div className="md:hidden rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm overflow-hidden animate-pulse">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 space-y-2">
            <div className="h-2.5 w-24 rounded bg-slate-200" />
            <div className="h-5 w-44 rounded bg-slate-200" />
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="h-3 w-full max-w-xs rounded bg-slate-100" />
            <div className="h-8 w-36 rounded-lg bg-slate-100" />
          </div>
        </div>
        <div
          className={`grid grid-cols-1 gap-3 animate-pulse sm:gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 ${isMembre ? 'hidden md:grid' : ''}`}
        >
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200" />
              <div className="h-4 w-2/3 rounded bg-slate-200" />
              <div className="h-3 w-full rounded bg-slate-100" />
              <div className="h-3 w-4/5 rounded bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="hidden md:block">
      <PageHeader
        title={isMembre ? t('documentsPage.libraryTitle') : t('documentsPage.pageTitle')}
        subtitle={
          isMembre
            ? t('documentsPage.libraryDesc')
            : docTab === 'entrea'
            ? t('documentsPage.subtitleEntrea')
            : t('documentsPage.subtitlePlatform')
        }
        actions={
          docTab === 'entrea' && canCreateDocument ? (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition font-medium text-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
            >
              {t('documentsPage.addDocument')}
            </button>
          ) : null
        }
      />
      </div>

      <MemberMobileShell>
        <MemberMobileHero
          label={t('documentsPage.mobileKicker')}
          title={String(documents.length)}
          hint={docTab === 'entrea' ? t('documentsPage.libraryTitle') : t('documentsPage.cloudTitle')}
          tone="info"
          actions={
            canCreateDocument ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="mm-cta flex-1"
              >
                {t('documentsPage.addDocument')}
              </button>
            ) : null
          }
        />
      </MemberMobileShell>

      {!isMembre ? (
      <div className="-mx-1 flex gap-2 overflow-x-auto border-b border-slate-200 pb-3 touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:flex-wrap">
        <button
          type="button"
          onClick={() => setDocTab('entrea')}
          className={`mm-chip shrink-0 snap-start min-h-[44px] px-3.5 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
            docTab === 'entrea'
              ? 'mm-chip--on shadow-sm'
              : 'mm-chip--off'
          }`}
        >
          {t('documentsPage.tabEntrea')}
        </button>
        <button
          type="button"
          onClick={() => setDocTab('platform')}
          className={`mm-chip shrink-0 snap-start min-h-[44px] px-3.5 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4] ${
            docTab === 'platform'
              ? 'mm-chip--on shadow-sm'
              : 'mm-chip--off'
          }`}
        >
          {t('documentsPage.tabPlatform')}
        </button>
      </div>
      ) : null}

      {docTab === 'platform' && (
        <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3 sm:rounded-lg sm:p-6 md:space-y-4">
          <p className="text-xs leading-snug text-gray-600 md:text-sm">
            {t('documentsPage.platformBlurb')}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center px-4 py-2 bg-[#edf4ff] text-[#113a5b] rounded-lg cursor-pointer hover:bg-[#e4eefb] text-sm font-medium border border-[#bcd4f6]">
              <input
                type="file"
                className="hidden"
                disabled={platformUploading}
                onChange={handlePlatformUpload}
              />
              {platformUploading ? t('documentsPage.uploading') : t('documentsPage.uploadFile')}
            </label>
          </div>
          {platformLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2f6fd4]" />
            </div>
          ) : platformFiles.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">{t('documentsPage.platformEmpty')}</p>
          ) : (
            <>
              {canBulkPlatformFiles && selectedPlatformFileIds.length > 0 && (
                <div className="mb-4 bg-[#edf4ff] rounded-lg shadow-sm p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-[#bcd4f6]">
                  <span className="text-sm text-gray-900 font-semibold flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#113a5b] text-white flex items-center justify-center text-xs font-bold">
                      {selectedPlatformFileIds.length}
                    </div>
                    {t('documentsPage.filesSelected')}
                  </span>
                  <button
                    type="button"
                    onClick={handleBulkPlatformDelete}
                    className="px-3 py-1.5 text-xs sm:text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition font-medium"
                  >
                    {t('documentsPage.delete')}
                  </button>
                </div>
              )}
              <div className="md:hidden space-y-2">
                {platformFiles.map((f) => (
                  <div
                    key={f.id}
                    className="rounded-xl border border-slate-200 bg-slate-50/50 p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
                        {f.filename || f.name || '—'}
                      </p>
                      {canBulkPlatformFiles && (
                        <TableListCheckbox
                          checked={selectedPlatformFileIds.includes(f.id)}
                          onChange={() => togglePlatformFileSelection(f.id)}
                          aria-label={t('documentsPage.ariaSelectFile', { name: f.filename || f.name || f.id })}
                        />
                      )}
                    </div>
                    <p className="mt-1 font-mono text-[10px] text-gray-500 break-all">{f.id}</p>
                    <p className="mt-0.5 text-xs text-gray-600">{f.content_type || '—'}</p>
                    {hasPermission('documents.delete') && (
                      <button
                        type="button"
                        onClick={() => handlePlatformDelete(f.id)}
                        className="mt-2 text-xs font-semibold text-red-600"
                      >
                        {t('documentsPage.delete')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      {canBulkPlatformFiles && (
                        <th className="py-2 pr-2 w-10">
                          <TableListCheckbox
                            onChange={handleSelectAllPlatformFiles}
                            checked={
                              platformFiles.length > 0 &&
                              selectedPlatformFileIds.length === platformFiles.length
                            }
                            aria-label={t('documentsPage.ariaSelectAll')}
                          />
                        </th>
                      )}
                      <th className="py-2 pr-4">{t('documentsPage.tableFile')}</th>
                      <th className="py-2 pr-4">{t('documentsPage.tableId')}</th>
                      <th className="py-2 pr-4">{t('documentsPage.tableType')}</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {platformFiles.map((f) => (
                      <tr key={f.id} className="border-b border-gray-100">
                        {canBulkPlatformFiles && (
                          <td className="py-2 pr-2 align-middle">
                            <TableListCheckbox
                              checked={selectedPlatformFileIds.includes(f.id)}
                              onChange={() => togglePlatformFileSelection(f.id)}
                              aria-label={t('documentsPage.ariaSelectFile', { name: f.filename || f.name || f.id })}
                            />
                          </td>
                        )}
                        <td className="py-2 pr-4 font-medium text-gray-900">{f.filename || f.name || '—'}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-gray-600">{f.id}</td>
                        <td className="py-2 pr-4 text-gray-600">{f.content_type || '—'}</td>
                        <td className="py-2 text-right">
                          {hasPermission('documents.delete') && (
                            <button
                              type="button"
                              onClick={() => handlePlatformDelete(f.id)}
                              className="text-red-600 hover:underline text-xs"
                            >
                              {t('documentsPage.delete')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {docTab === 'entrea' && (
        <>

      {/* Barre de recherche et filtres */}
      <SearchAndFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchInputClassName="min-h-[44px] md:min-h-0"
        collapseSearchOnMobile
        mobileFiltersToggleLabel="Filtres"
        mobileFiltersToggleLabel={isMembre ? t('documentsPage.filtersMobileLabel') : undefined}
        className={isMembre ? 'max-md:!space-y-2 max-md:!p-2.5' : ''}
        placeholder={isMembre ? t('documentsPage.searchPhMember') : t('documentsPage.searchPhAdmin')}
        filters={[
          {
            key: 'category',
            label: t('documentsPage.categoryLabel'),
            type: 'select',
            value: selectedCategory,
            onChange: setSelectedCategory,
            options: [
              { value: 'all', label: t('documentsPage.catAll') },
              ...Array.from(new Set(documents.map(d => d.category))).filter(c => c).map(cat => ({
                value: cat.toLowerCase(),
                label: cat
              }))
            ]
          },
          ...(!isMembre
            ? [{
                key: 'visibility',
                label: t('documentsPage.visibilityLabel'),
                type: 'button-group',
                value: selectedVisibility,
                onChange: setSelectedVisibility,
                options: [
                  { value: 'all', label: t('documentsPage.visAll') },
                  { value: 'public', label: t('documentsPage.visPublic') },
                  { value: 'private', label: t('documentsPage.visPrivate') }
                ]
              }]
            : [])
        ]}
      />

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {renderDocumentsPagination('top')}
      <div className="grid grid-cols-1 gap-2.5 sm:gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 p-3">
        {filteredDocuments.map((doc) => {
          const cat = String(doc.category || '').toLowerCase();
          const catPill =
            cat === 'rapport'
              ? 'bg-[#edf4ff] text-[#113a5b] ring-1 ring-[#bcd4f6]'
              : cat === 'contrat'
                ? 'bg-slate-100 text-slate-800 ring-1 ring-slate-200'
                : cat === 'financier'
                  ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
                  : 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
          return (
          <div
            key={doc.id}
            className="mm-card mm-press p-4 transition-shadow md:rounded-lg md:border-gray-200 md:bg-white md:p-6 md:shadow-sm md:[backdrop-filter:none]"
          >
            <div className="relative">
            <div className="mb-3 flex items-start justify-between md:mb-4">
              <div className="rounded-lg bg-[#edf4ff] p-2 text-[#113a5b] md:rounded-xl md:p-3">
                <svg className="h-6 w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getCategoryIcon(doc.category)} />
                </svg>
              </div>
              <div className="flex flex-col items-end gap-1.5 md:gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold md:px-3 md:py-1.5 md:text-xs ${catPill}`}>
                  {doc.category}
                </span>
                {doc.is_public && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-900 ring-1 ring-emerald-200 md:px-3 md:py-1 md:text-xs">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {t('documentsPage.publicBadge')}
                  </span>
                )}
              </div>
            </div>

            <h3 className="mb-1 text-[15px] font-semibold leading-snug text-[#0f3350] md:mb-2 md:text-lg">{doc.title}</h3>
            {doc.description && (
              <p className="mb-2 line-clamp-2 text-[11px] text-gray-600 md:mb-4 md:text-sm">{doc.description}</p>
            )}
            
            <div className="mb-3 text-[10px] text-gray-500 md:mb-4 md:text-xs">
              {new Date(doc.uploaded_at).toLocaleDateString(localeFmt)}
            </div>

            <div className="flex items-center gap-2">
              {doc.file_url && (
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2.5 bg-[#edf4ff] text-[#113a5b] rounded-lg hover:bg-[#e4eefb] border border-[#bcd4f6] transition text-center font-medium text-sm min-h-[44px] flex items-center justify-center"
                >
                  {t('documentsPage.openLink')}
                </a>
              )}
              {hasPermission('documents.edit') && (
              <button
                onClick={() => {
                  setSelectedDocument(doc);
                  setFormData(doc);
                  setShowForm(true);
                }}
                aria-label={t('documentsPage.editAria', { title: doc.title || '' }).trim()}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                  title={t('documentsPage.editTitle')}
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
              )}
              {hasPermission('documents.delete') && (
              <button
                onClick={() => handleDelete(doc.id)}
                aria-label={t('documentsPage.deleteAria', { title: doc.title || '' }).trim()}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                  title={t('documentsPage.deleteTitleShort')}
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
              )}
            </div>
            </div>
          </div>
          );
        })}
      </div>

      {filteredDocuments.length === 0 && documents.length > 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="mt-4 text-gray-900 font-medium">{t('documentsPage.emptySearchTitle')}</p>
          <p className="mt-2 text-sm text-gray-500">{t('documentsPage.emptySearchHint')}</p>
        </div>
      )}

      {documents.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('documentsPage.emptyLibTitle')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('documentsPage.emptyLibHint')}</p>
        </div>
      )}

        {renderDocumentsPagination('bottom')}
      </div>

      {docTab === 'entrea' && canCreateDocument && (
        <div className="md:hidden fixed right-4 z-40 bottom-[calc(env(safe-area-inset-bottom)+8.75rem)]">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            aria-label={t('documentsPage.fabAddAria')}
            className="mm-cta h-12 rounded-2xl px-4 text-sm shadow-lg"
          >
            {t('documentsPage.fabAdd')}
          </button>
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">{t('documentsPage.pageTitle', 'Documents')}</p>
              <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {selectedDocument ? t('documentsPage.modalEditTitle') : t('documentsPage.modalNewTitle')}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label={t('documentsPage.cancel')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
              <div className="mm-card space-y-3 p-3 sm:p-3.5">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('documentsPage.labelTitle')}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mm-field"
                    required
                    placeholder={t('documentsPage.phTitle')}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('documentsPage.labelDescription')}
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mm-field"
                    rows={3}
                    placeholder={t('documentsPage.phDescription')}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('documentsPage.labelCategory')}
                  </label>
                  <MmSelect
                    value={formData.category}
                    onChange={(category) => setFormData({ ...formData, category })}
                    required
                    placeholder={t('documentsPage.selectCategory')}
                    options={[
                      { value: 'Rapport', label: t('documentsPage.catRapport') },
                      { value: 'Contrat', label: t('documentsPage.catContrat') },
                      { value: 'Financier', label: t('documentsPage.catFinancier') },
                      { value: 'Administratif', label: t('documentsPage.catAdministratif') },
                      { value: 'Autre', label: t('documentsPage.catAutre') },
                    ]}
                  />
                </div>
              </div>

              <div className="mm-card space-y-3 p-3 sm:p-3.5">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('documentsPage.labelFileUrl')}
                  </label>
                  <input
                    type="url"
                    value={formData.file_url || ''}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    className="mm-field"
                    placeholder={t('documentsPage.phFileUrl')}
                  />
                  <p className="mt-1.5 text-xs text-slate-500">{t('documentsPage.fileUrlHelp')}</p>
                </div>

                <label className="flex items-center gap-3 rounded-xl border border-slate-200/90 bg-white/70 px-3 py-2.5">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={formData.is_public || false}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-[#113a5b] focus:ring-[#2f6fd4]"
                  />
                  <span className="text-sm font-medium text-[#0f3350]">
                    {t('documentsPage.publicDocLabel')}
                  </span>
                </label>
              </div>
            </div>

            <div className="mm-modal-footer">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="mm-cta mm-cta--ghost flex-1"
              >
                {t('documentsPage.cancel')}
              </button>
              <button type="submit" className="mm-cta flex-1">
                {selectedDocument ? t('documentsPage.update') : t('documentsPage.create')}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

        </>
      )}
    </div>
  );
};

export default Documents;